import React from "react";
import { Box, Dialog, IconButton, useTheme } from "@mui/material";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import ActivityWizard from "../Activity/ActivityWizard";
import { INITIAL_DATA } from "../AddActivity";
import { ActivityWizardData } from "../AddActivity/types";
import { useSaveDraft } from "../hooks/useSaveDraft";

export interface CreatedDraftLo {
  id: number;
  title: string;
  type: string;
  entity: Record<string, any>;
}

interface CreateActivityModalCstProps {
  open: boolean;
  onClose: () => void;
  onCreated: (item: CreatedDraftLo) => void;
}

interface ContentProps {
  onClose: () => void;
  onCreated: (item: CreatedDraftLo) => void;
}

const CreateActivityModalContent: React.FC<ContentProps> = ({
  onClose,
  onCreated,
}) => {
  const { t } = useTranslation("wizard-activity");
  const { id: corporateId = "" } = useParams();
  const { saveDraft, isSavingDraft } = useSaveDraft({
    corporateId,
    type: "activity",
  });

  const handleComplete = async (data: ActivityWizardData) => {
    const res = await saveDraft(data);
    onCreated({
      id: res.id,
      title: data.title,
      type: data.type || "",
      entity: { ...data, id: res.id, is_draft: true },
    });
    onClose();
  };

  return (
    <ActivityWizard
      embedded
      mode="add"
      initialData={INITIAL_DATA}
      onComplete={handleComplete}
      isSubmitting={isSavingDraft}
      completeLabel={t("confirm") || "Conferma"}
      syncNavbarInitiative={false}
    />
  );
};

const CreateActivityModalCst: React.FC<CreateActivityModalCstProps> = ({
  open,
  onClose,
  onCreated,
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <IconButton
        onClick={onClose}
        aria-label="close"
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          color: theme.palette.text.primary,
          zIndex: 1,
        }}
      >
        <X size={20} />
      </IconButton>
      <Box sx={{ px: 3, pt: 2, pb: 2 }}>
        {open && (
          <CreateActivityModalContent onClose={onClose} onCreated={onCreated} />
        )}
      </Box>
    </Dialog>
  );
};

export default CreateActivityModalCst;
